export const MONEY_REGEX = /^[0-9]*(\.[0-9][0-9]?)?$/gm;
export const TICKET_REGEX = /^[0-9A-Za-z]{4}$/gm;
export const PHONE_REGEX = /^[0-9]{10}$/gm;
export const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/gm;
export const ZIPCODE_REGEX = /^[0-9]{5}$/gm;

export interface ValidationResult {
  isError: boolean;
  message: string;
}

export const defaultValidationResult: ValidationResult = {
  isError: false,
  message: "",
};

type _BasicValidation = (data: string, ...params: any[]) => ValidationResult;

export type Validation = (object: any, field: string) => ValidationResult;

export type FormFieldSetter = (
  field: string,
  summary: ValidationResult,
) => void;

export type FieldValidation = (
  object: any,
  formResultFieldSetter: FormFieldSetter,
) => ValidationResult;

export type FormValidationResult = {
  isError: boolean;
  fields: { [key: string]: ValidationResult };
};

export const initValidation = (...fields: string[]): FormValidationResult => {
  const state: FormValidationResult = { isError: false, fields: {} };
  fields.map((field) => (state.fields[field] = defaultValidationResult));
  return state;
};

export const _asValidationResult = (
  isError: boolean,
  message: string,
): ValidationResult => {
  return {
    isError: isError,
    message: message,
  };
};

/* basic validation building blocks */

export const _isEmpty: _BasicValidation = (
  data: string,
  message: string,
): ValidationResult => _asValidationResult(data === "", message);

export const _isNotExactLength: _BasicValidation = (
  data: string,
  length: number,
  message: string,
): ValidationResult => _asValidationResult(data?.length !== length, message);

export const _isNotMatch: _BasicValidation = (
  data: string,
  regex: RegExp,
  message: string,
): ValidationResult =>
  _asValidationResult(data.match(regex)?.length !== 1, message);

/* validation test for fields of objects  */

export const isEmpty =
  (message: string): Validation =>
  (object: any, field: string): ValidationResult =>
    _isEmpty(object[field], message);

export const isNotExactLength =
  (length: number, message: string): Validation =>
  (object: any, field: string) =>
    _isNotExactLength(object[field], length, message);

export const isNotMatch =
  (regex: RegExp, message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], regex, message);

export const isNotMoney =
  (message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], MONEY_REGEX, message);

export const isNotTicket =
  (message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], TICKET_REGEX, message);

export const isNotPhone =
  (message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], PHONE_REGEX, message);

export const isNotEmail =
  (message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], EMAIL_REGEX, message);

export const isNotZipcode =
  (message: string): Validation =>
  (object: any, field: string) =>
    _isNotMatch(object[field], ZIPCODE_REGEX, message);

export const isNotEqualToField =
  (otherField: string, message: string): Validation =>
  (object: any, field: string) =>
    _asValidationResult(object[field] !== object[otherField], message);

/* *** */

export const isAnyError = (testResults: ValidationResult[]) =>
  testResults.reduce((accum, result) => accum || result.isError, false);

export const summarizeFieldResults = (testResults: ValidationResult[]) => {
  const errors = testResults.filter((result) => result.isError);
  const message = errors.length > 0 ? errors[0].message : "";
  return _asValidationResult(isAnyError(testResults), message);
};

// run all validation tests for a single field
export const validate =
  (field: string, ...testDefs: Validation[]): FieldValidation =>
  (
    object: any,
    formResultFieldSetter: (field: string, summary: ValidationResult) => void,
  ): ValidationResult => {
    const results = testDefs.map((test) => test(object, field));
    const fieldResults = summarizeFieldResults(results);
    formResultFieldSetter(field, fieldResults);
    return fieldResults;
  };

export const findErrors = (object: any, ...validations: FieldValidation[]) => {
  const errorState: FormValidationResult = { isError: false, fields: {} };

  const fieldSetter = (field: string, result: ValidationResult) => {
    errorState.fields[field] = result;
  };

  const results = validations.map((validateAndSet) =>
    validateAndSet(object, fieldSetter),
  );

  errorState.isError = isAnyError(results);

  return errorState;
};
